import type { PiiFormData } from "@porchpass/ui";
import type { UpdateBorrowerStatusFormData } from "src/types/borrower";
import type { DtoBorrower } from "@porchpass/api-client";
import type { ConsentFormData } from "src/types/consent";
import { fieldErrorsAfterSubmit } from "src/forms/fieldErrorsAfterSubmit";
import { createIsoDate, parseIsoDate, safeNumber } from "src/utils";

export function toBankruptcyPiiFormData(
  dto: UpdateBorrowerStatusFormData,
): PiiFormData {
  const hasMonthYear =
    Boolean(dto.bankruptcy_month) && Boolean(dto.bankruptcy_year);
  const hasDeclaredNo =
    dto.bankruptcy_month === "" &&
    dto.bankruptcy_year === "" &&
    ("bankruptcy_month" in dto || "bankruptcy_year" in dto);

  let hasBankruptcy: boolean | undefined;
  if (dto.has_declared_bankruptcy !== undefined) {
    hasBankruptcy = dto.has_declared_bankruptcy;
  } else if (hasMonthYear) {
    hasBankruptcy = true;
  } else if (hasDeclaredNo) {
    hasBankruptcy = false;
  }

  let bankruptcyDate: string | undefined;
  if (hasMonthYear) {
    const iso = createIsoDate(
      safeNumber(dto.bankruptcy_year),
      safeNumber(dto.bankruptcy_month),
    );
    bankruptcyDate = iso.split("T")[0];
  }

  return {
    hasBankruptcy,
    bankruptcyDate,
  };
}

export function fromBankruptcyPiiFormData(
  patch: Partial<PiiFormData>,
): Partial<UpdateBorrowerStatusFormData> {
  const result: Partial<UpdateBorrowerStatusFormData> = {};

  if (patch.hasBankruptcy !== undefined) {
    result.has_declared_bankruptcy = patch.hasBankruptcy;
  }

  if (patch.hasBankruptcy === false) {
    result.bankruptcy_month = "";
    result.bankruptcy_year = "";
    return result;
  }

  if (patch.bankruptcyDate) {
    const isoDate = patch.bankruptcyDate.includes("T")
      ? patch.bankruptcyDate
      : `${patch.bankruptcyDate}T00:00:00-05:00`;
    const { month, year } = parseIsoDate(isoDate);
    result.bankruptcy_month = month;
    result.bankruptcy_year = year;
    result.has_declared_bankruptcy = true;
  }

  return result;
}

export function bankruptcyPiiErrorsAfterSubmit(
  formErrors: {
    bankruptcy_month?: { message?: string };
    bankruptcy_year?: { message?: string };
  },
  hasAttemptedFormSubmission: boolean,
): { bankruptcyDate?: string } {
  if (!hasAttemptedFormSubmission) {
    return {};
  }

  return {
    bankruptcyDate:
      formErrors.bankruptcy_month?.message ??
      formErrors.bankruptcy_year?.message,
  };
}

export function getConsentSsnDigits(dto: ConsentFormData): string {
  return `${dto.ssn_start ?? ""}${dto.ssn_middle ?? ""}${dto.ssn_end ?? ""}`.replace(
    /\D/g,
    "",
  );
}

export function formatConsentSsnForApi(dto: ConsentFormData): string {
  const digits = getConsentSsnDigits(dto);
  return `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5)}`;
}

const CONSENT_SSN_API_ERROR_MESSAGES: Record<string, string> = {
  "borrower with same ssn already exists":
    "This Social Security Number is already used by another borrower on this application.",
};

/** Map consent API errors that belong on the SSN field. */
export function getConsentSsnFieldError(
  apiError: string | null | undefined,
): string | undefined {
  if (!apiError) {
    return undefined;
  }

  if (CONSENT_SSN_API_ERROR_MESSAGES[apiError]) {
    return CONSENT_SSN_API_ERROR_MESSAGES[apiError];
  }

  const lower = apiError.toLowerCase();
  if (lower.includes("ssn") || lower.includes("social security")) {
    return apiError;
  }

  return undefined;
}

export function toConsentFormData(dto: DtoBorrower): ConsentFormData {
  const result: ConsentFormData = {};

  if (dto.ssn) {
    Object.assign(result, fromConsentPiiFormData({ ssn: dto.ssn }));
  }

  if (dto.date_of_birth) {
    const dateOfBirth = dto.date_of_birth.includes("T")
      ? dto.date_of_birth.split("T")[0]
      : dto.date_of_birth;
    Object.assign(result, fromConsentPiiFormData({ dateOfBirth }));
  }

  if (dto.acknowledged_at) {
    result.is_acknowledged = true;

    const nameParts = [dto.first_name, dto.middle_name, dto.last_name].filter(
      Boolean,
    );
    if (nameParts.length > 0) {
      result.disclaimer = nameParts.join(" ");
    }
  }

  return result;
}

export function buildConsentSubmitData(
  formData: ConsentFormData,
  piiValue: Pick<PiiFormData, "ssn" | "dateOfBirth">,
): ConsentFormData {
  return {
    ...formData,
    ...fromConsentPiiFormData(piiValue),
  };
}

export function toConsentPiiFormData(dto: ConsentFormData): PiiFormData {
  const ssnDigits = getConsentSsnDigits(dto);

  let dateOfBirth: string | undefined;
  if (dto.dob_year && dto.dob_month && dto.dob_day) {
    dateOfBirth = createIsoDate(
      safeNumber(dto.dob_year),
      safeNumber(dto.dob_month),
      safeNumber(dto.dob_day),
    ).split("T")[0];
  }

  return {
    ssn: ssnDigits || undefined,
    dateOfBirth,
  };
}

export function fromConsentPiiFormData(
  patch: Partial<PiiFormData>,
): Partial<ConsentFormData> {
  const result: Partial<ConsentFormData> = {};

  if (patch.ssn !== undefined) {
    const digits = patch.ssn.replace(/\D/g, "");
    result.ssn_start = digits.slice(0, 3);
    result.ssn_middle = digits.slice(3, 5);
    result.ssn_end = digits.slice(5, 9);
  }

  if (patch.dateOfBirth) {
    const isoDate = patch.dateOfBirth.includes("T")
      ? patch.dateOfBirth
      : `${patch.dateOfBirth}T00:00:00-05:00`;
    const { year, month, day } = parseIsoDate(isoDate);
    result.dob_year = year;
    result.dob_month = month;
    result.dob_day = day;
  }

  return result;
}

const firstNonEmptyMessage = (
  ...messages: Array<string | undefined>
): string | undefined => {
  for (const message of messages) {
    if (message && message.trim().length > 0) {
      return message;
    }
  }

  return undefined;
};

export function consentPiiErrorsAfterSubmit(
  formErrors: {
    ssn_start?: { message?: string };
    ssn_middle?: { message?: string };
    ssn_end?: { message?: string };
    dob_month?: { message?: string };
    dob_day?: { message?: string };
    dob_year?: { message?: string };
  },
  hasAttemptedFormSubmission: boolean,
) {
  return fieldErrorsAfterSubmit(
    {
      ssn: firstNonEmptyMessage(
        formErrors.ssn_start?.message,
        formErrors.ssn_middle?.message,
        formErrors.ssn_end?.message,
      ),
      dateOfBirth: firstNonEmptyMessage(
        formErrors.dob_month?.message,
        formErrors.dob_day?.message,
        formErrors.dob_year?.message,
      ),
    },
    hasAttemptedFormSubmission,
  );
}
